import React, { useCallback, useEffect, useMemo } from 'react';
import { Alert, Modal } from 'react-bootstrap';
import { ClinicalButton, ClinicalModalActions } from './ui';
import { CARE_REFERRAL_CATEGORIES } from '../../constants/careReferralCategories';
import { MEDICAL_COVERAGE_TYPES } from '../../constants/medicalExamCategories';
import {
  formatConsultationDate,
  formatPrintDate,
  getCategoryLabelPlural,
  normalizeCategory,
  referralMetaLine
} from './utils/careReferralFormat';
import './ConsultationCareReferralsViewModal.css';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo.png`;

const CATEGORY_COPY = {
  [CARE_REFERRAL_CATEGORIES.HOSPITAL_REFERRAL.id]: {
    modalTitle: 'Bon d\'orientation — hôpital',
    docTitle: 'Bon d\'orientation hospitalière',
    foot: 'FUNTOA SMIE — document à remettre à l\'établissement partenaire.'
  },
  [CARE_REFERRAL_CATEGORIES.OPHTHALMOLOGY.id]: {
    modalTitle: 'Bon de prise en charge — ophtalmologie',
    docTitle: 'Bon de demande — ophtalmologie',
    foot: 'FUNTOA SMIE — document à remettre au partenaire ophtalmologique.'
  },
  [CARE_REFERRAL_CATEGORIES.PEDIATRICS.id]: {
    modalTitle: 'Bon d\'orientation — pédiatrie',
    docTitle: 'Bon de demande — pédiatrie',
    foot: 'FUNTOA SMIE — document à remettre au partenaire pédiatrique.'
  },
  [CARE_REFERRAL_CATEGORIES.OPTICAL.id]: {
    modalTitle: 'Bon de prise en charge — lunettes',
    docTitle: 'Bon de prise en charge optique',
    foot: 'FUNTOA SMIE — document à remettre à l\'opticien partenaire.'
  }
};

function ReferralList({ rows }) {
  if (!rows.length) {
    return <p className="cr-simple__none">— Aucune orientation —</p>;
  }
  return (
    <ol className="cr-simple__list">
      {rows.map((row, index) => {
        const meta = referralMetaLine(row);
        const coverage = MEDICAL_COVERAGE_TYPES[row.coverage_type]?.label || row.coverage_type;
        return (
          <li key={row.catalog_referral_id || index} className="cr-simple__item">
            <span className="cr-simple__exam">{row.referral_name}</span>
            {row.partner_name ? (
              <div className="cr-simple__sub">
                <strong>Partenaire :</strong> {row.partner_name}
              </div>
            ) : null}
            <div className="cr-simple__sub">{coverage}</div>
            {meta ? <div className="cr-simple__sub">{meta}</div> : null}
          </li>
        );
      })}
    </ol>
  );
}

export default function ConsultationCareReferralsViewModal({
  show,
  onHide,
  consultation,
  referrals = [],
  category,
  clinicalInformation = '',
  previewUnsaved = false
}) {
  const cat = normalizeCategory(category);
  const copy = CATEGORY_COPY[cat] || CATEGORY_COPY[CARE_REFERRAL_CATEGORIES.HOSPITAL_REFERRAL.id];
  const sectionTitle = getCategoryLabelPlural(cat);

  const filteredReferrals = useMemo(
    () => referrals.filter((r) => normalizeCategory(r.category) === cat),
    [referrals, cat]
  );

  const clinicalText = (clinicalInformation || '').trim();

  const runPrint = useCallback(() => {
    document.body.classList.add('care-referral-print-mode');
    const cleanup = () => {
      document.body.classList.remove('care-referral-print-mode');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.setTimeout(() => window.print(), 150);
  }, []);

  useEffect(() => {
    if (!show) document.body.classList.remove('care-referral-print-mode');
  }, [show]);

  const patientName = (consultation?.patient_name || '').trim() || '—';
  const doctorName = (consultation?.doctor_name || '').trim() || '—';

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      className="care-referral-view-modal clinical-view-modal"
    >
      <Modal.Header closeButton className="care-referral-view-modal__head recv-no-print">
        <div className="d-flex align-items-center gap-2">
          <img src={LOGO_SRC} alt="" className="care-referral-view-modal__head-logo" />
          <Modal.Title className="fs-6 mb-0">{copy.modalTitle}</Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body className="care-referral-view-modal__body">
        <ClinicalModalActions>
          <ClinicalButton variant="secondary" size="sm" onClick={runPrint}>
            Imprimer
          </ClinicalButton>
          <ClinicalButton variant="primary" size="sm" icon="download" onClick={runPrint}>
            PDF
          </ClinicalButton>
        </ClinicalModalActions>

        {previewUnsaved ? (
          <Alert variant="warning" className="py-2 small recv-no-print">
            Aperçu non enregistré — enregistrez avant impression officielle.
          </Alert>
        ) : null}

        <article className="cr-simple" id="care-referral-print-sheet">
          <header className="cr-simple__header">
            <img src={LOGO_SRC} alt="FUNTOA SMIE" className="cr-simple__logo" />
            <div>
              <h1 className="cr-simple__brand">FUNTOA SMIE</h1>
              <p className="cr-simple__doctype">{copy.docTitle}</p>
            </div>
          </header>

          <p className="cr-simple__meta">
            Consultation n° <strong>{consultation?.id ?? '—'}</strong>
            {' · '}
            {formatConsultationDate(consultation)}
            {' · '}
            édité le {formatPrintDate()}
          </p>

          <div className="cr-simple__info">
            <p>
              <strong>Patient :</strong> {patientName}
              {consultation?.patient_age != null ? `, ${consultation.patient_age} ans` : ''}
            </p>
            {consultation?.patient_phone ? (
              <p>
                <strong>Téléphone :</strong> {consultation.patient_phone}
              </p>
            ) : null}
          </div>

          <div className="cr-simple__clinical">
            <p className="cr-simple__clinical-label">Renseignement clinique</p>
            <p className="cr-simple__clinical-text">{clinicalText || '—'}</p>
          </div>

          <div className="cr-simple__block">
            <h2 className="cr-simple__heading">{sectionTitle}</h2>
            <ReferralList rows={filteredReferrals} />
          </div>

          <div className="cr-simple__sign">
            <p>Signature du médecin</p>
            <div className="cr-simple__sign-line">&nbsp;</div>
          </div>

          <p className="cr-simple__foot">{copy.foot}</p>
          {doctorName !== '—' ? (
            <p className="cr-simple__doctor-quiet">Médecin prescripteur : {doctorName}</p>
          ) : null}
        </article>
      </Modal.Body>

      <Modal.Footer className="recv-no-print">
        <ClinicalButton variant="ghost" size="sm" onClick={onHide}>
          Fermer
        </ClinicalButton>
        <ClinicalButton variant="primary" size="sm" icon="download" onClick={runPrint}>
          Exporter en PDF
        </ClinicalButton>
      </Modal.Footer>
    </Modal>
  );
}
